import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { Image as ImageIcon, CheckCircle, UploadCloud, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";

const CATEGORIES = [
  { id: "development", label: "Development" },
  { id: "frontend", label: "Frontend" },
  { id: "backend", label: "Backend" },
];

const BlogEditor = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: "", excerpt: "", content: "", category: "development", tags: "", image: null, published: false,
  });

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/blog/${slug}`);
        const post = response.data;

        setFormData({
          title: post.title || "", 
          excerpt: post.excerpt || "", 
          content: post.content || "", 
          category: post.category || "development", 
          tags: post.tags?.join(", ") || "", 
          published: post.published || false,
        });

        if (post.image) {
          setImagePreview(post.image.startsWith("http") ? post.image : `${apiUrl}/${post.image}`);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch post. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, apiUrl]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file" && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "tags") formDataToSend.append(key, JSON.stringify(formData[key].split(",").map((t) => t.trim())));
        else if (key === "image" && formData[key]) formDataToSend.append(key, formData[key]);
        else if (key !== "image") formDataToSend.append(key, formData[key]);
      });

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication required");

      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" };
      const response = slug 
        ? await axios.put(`${apiUrl}/api/blog/${slug}`, formDataToSend, { headers })
        : await axios.post(`${apiUrl}/api/blog`, formDataToSend, { headers });

      navigate(`/blog/${response.data.slug}`);
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred while saving the post");
    } finally {
      setLoading(false);
    }
  };

  const inputBaseClass = "w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-all duration-200 bg-zinc-50 dark:bg-[#09090B] border-zinc-200 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-600";

  if (loading && slug) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#FAFAFA] dark:bg-[#09090B]">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 bg-[#FAFAFA] dark:bg-[#09090B] px-4 sm:px-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-4xl mx-auto p-8 md:p-12 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 shadow-sm"
      >
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-4">
          <h1 className="text-3xl font-display font-semibold tracking-tight text-zinc-900 dark:text-white">
            {slug ? "Edit Manuscript" : "Draft New Post"}
          </h1>
          {slug && (
            <span className="flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 w-fit">
              <CheckCircle className="w-3.5 h-3.5 mr-1.5"/> Active Session
            </span>
          )}
        </div>

        {error && (
          <div className="flex items-center p-4 mb-8 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-xl">
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Title</label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} className={inputBaseClass} required placeholder="E.g., Architecting a Scalable Next.js Application" />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Excerpt</label>
            <textarea name="excerpt" value={formData.excerpt} onChange={handleChange} rows="2" className={`${inputBaseClass} resize-none`} required placeholder="A concise 1-2 sentence summary..." />
          </div>

          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Content <span className="text-zinc-400 dark:text-zinc-500 font-normal ml-1">(Markdown Supported)</span></label>
            <textarea name="content" value={formData.content} onChange={handleChange} rows="12" className={`${inputBaseClass} font-mono text-sm leading-relaxed`} required placeholder="Start writing your engineering insights here..." />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Category</label>
              <select name="category" value={formData.category} onChange={handleChange} className={inputBaseClass} required>
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Tags</label>
              <input type="text" name="tags" value={formData.tags} onChange={handleChange} className={inputBaseClass} placeholder="React, NextJS, Architecture" />
            </div>
          </div>

          {/* Refined Image Upload Dropzone */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Featured Media</label>
            <div className="relative group p-8 border-2 border-dashed rounded-2xl border-zinc-300 dark:border-zinc-700 hover:border-emerald-500 dark:hover:border-emerald-500/50 transition-colors bg-zinc-50 dark:bg-zinc-950">
              <input type="file" name="image" onChange={handleChange} accept="image/*" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" required={!slug} />
              
              {!imagePreview ? (
                <div className="flex flex-col items-center justify-center text-center">
                  <UploadCloud className="w-10 h-10 mb-3 text-zinc-400 group-hover:text-emerald-500 transition-colors" />
                  <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Click or drag image to upload</p>
                  <p className="text-xs text-zinc-500 mt-1">SVG, PNG, JPG or GIF (max. 5MB)</p>
                </div>
              ) : (
                <div className="relative rounded-xl overflow-hidden shadow-sm group-hover:shadow-md transition-all">
                  <img src={imagePreview} alt="Preview" className="h-64 w-full object-cover" />
                  <div className="absolute inset-0 bg-zinc-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                    <div className="text-white flex items-center gap-2 font-medium">
                      <ImageIcon className="w-5 h-5" /> Change Media
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Premium Toggle Checkbox */}
          <div className="flex items-center pt-4">
            <div className="relative flex items-center">
              <input type="checkbox" name="published" id="published" checked={formData.published} onChange={handleChange} className="peer sr-only" />
              <div className="w-11 h-6 bg-zinc-200 dark:bg-zinc-800 rounded-full peer peer-checked:bg-emerald-500 transition-colors cursor-pointer"></div>
              <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5 shadow-sm pointer-events-none"></div>
            </div>
            <label htmlFor="published" className="ml-3 block text-sm font-medium text-zinc-700 dark:text-zinc-300 cursor-pointer">
              Publish immediately upon saving
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-8 border-t border-zinc-200 dark:border-zinc-800">
            <button type="button" onClick={() => navigate("/blog")} className="px-6 py-2.5 rounded-full font-medium text-sm transition-colors text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-white dark:hover:bg-white/5">
              Discard
            </button>
            <button type="submit" disabled={loading} className={`flex items-center justify-center px-6 py-2.5 rounded-full font-medium text-sm transition-all ${
              loading 
                ? "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500 cursor-not-allowed" 
                : "bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white shadow-lg shadow-zinc-900/20 dark:shadow-white/10"
            }`}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {loading ? "Saving..." : slug ? "Update Manuscript" : "Publish Post"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default BlogEditor;